<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import standardLauncherIcon from 'dashboard/assets/images/chat-round-dots.svg';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  widgetColor: {
    type: String,
    default: '#1f93ff',
  },
});

const emit = defineEmits(['update:widgetColor', 'previewChange']);

const { t } = useI18n();
const config = reactive({
  style: 'standard',
  label: '',
  position: 'right',
  profileName: '',
  profileTagline: '',
  widgetColor: '#1f93ff',
});

const styles = computed(() => [
  {
    value: 'profile_badge',
    title: t('INBOX_MGMT.WIDGET_BRAND_KIT.STYLES.PROFILE_BADGE.TITLE'),
    description: t(
      'INBOX_MGMT.WIDGET_BRAND_KIT.STYLES.PROFILE_BADGE.DESCRIPTION'
    ),
  },
  {
    value: 'branded_pill',
    title: t('INBOX_MGMT.WIDGET_BRAND_KIT.STYLES.TEXT_BUBBLE.TITLE'),
    description: t(
      'INBOX_MGMT.WIDGET_BRAND_KIT.STYLES.TEXT_BUBBLE.DESCRIPTION'
    ),
  },
]);

const positions = computed(() => [
  {
    value: 'left',
    label: t('INBOX_MGMT.WIDGET_BRAND_KIT.POSITION.LEFT'),
  },
  {
    value: 'right',
    label: t('INBOX_MGMT.WIDGET_BRAND_KIT.POSITION.RIGHT'),
  },
]);

const isProfileBadge = computed(() => config.style === 'profile_badge');
const isLegacyLauncher = computed(() => config.style === 'standard');

watch(
  () => props.inbox,
  inbox => {
    Object.assign(config, {
      style:
        inbox.launcher_config?.style === 'profile_badge' ||
        inbox.launcher_config?.style === 'branded_pill'
          ? inbox.launcher_config.style
          : 'standard',
      label: inbox.launcher_config?.label || '',
      position: inbox.launcher_config?.position || 'right',
      profileName: inbox.launcher_config?.profile_name || inbox.name || '',
      profileTagline: inbox.launcher_config?.profile_tagline || '',
      widgetColor: props.widgetColor || inbox.widget_color || '#1f93ff',
    });
  },
  { immediate: true, deep: true }
);

watch(
  () => props.widgetColor,
  widgetColor => {
    config.widgetColor = widgetColor || '#1f93ff';
  }
);

watch(
  () => config.widgetColor,
  widgetColor => emit('update:widgetColor', widgetColor)
);

watch(
  config,
  () => {
    const launcherConfig = {
      style: config.style,
      label: config.label,
      position: config.position,
      profileName: config.profileName,
      profileTagline: config.profileTagline,
      widgetColor: config.widgetColor,
    };
    emit('previewChange', launcherConfig);
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <section class="mb-8">
    <div class="mb-6">
      <div>
        <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.TITLE') }}
        </h2>
        <p class="max-w-2xl text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <div>
      <fieldset class="mb-6">
        <legend class="mb-3 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.STYLE_LABEL') }}
        </legend>
        <div class="grid gap-3 sm:grid-cols-2" role="radiogroup">
          <button
            v-for="style in styles"
            :key="style.value"
            type="button"
            role="radio"
            :aria-checked="config.style === style.value"
            class="relative rounded-lg border p-3 text-left transition-colors"
            :class="
              config.style === style.value
                ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand'
                : 'border-n-slate-6 hover:border-n-slate-8 dark:border-n-slate-7'
            "
            @click="config.style = style.value"
          >
            <span
              v-if="config.style === style.value"
              class="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-n-brand text-white"
            >
              <woot-icon icon="check" size="10" />
            </span>
            <span class="mb-3 flex h-10 items-center justify-center">
              <span
                v-if="style.value === 'profile_badge'"
                class="flex h-9 w-9 items-center justify-center rounded-full bg-n-brand"
              >
                <img
                  :src="standardLauncherIcon"
                  alt=""
                  class="h-5 w-5 brightness-0 invert"
                />
              </span>
              <span
                v-else
                class="rounded-full bg-n-brand px-3 py-2 text-xs font-medium text-white"
              >
                {{
                  config.label ||
                  $t('INBOX_MGMT.WIDGET_BRAND_KIT.PREVIEW_LABEL')
                }}
              </span>
            </span>
            <span class="block text-sm font-medium text-n-slate-12">{{
              style.title
            }}</span>
            <span class="mt-1 block text-xs leading-5 text-n-slate-11">{{
              style.description
            }}</span>
          </button>
        </div>
        <p v-if="isLegacyLauncher" class="mt-3 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.LEGACY_LAUNCHER_HINT') }}
        </p>
      </fieldset>

      <div class="grid gap-4 sm:grid-cols-2">
        <label class="block text-sm font-medium text-n-slate-12">
          <span class="mb-2 block">{{
            $t('INBOX_MGMT.WIDGET_BRAND_KIT.WIDGET_COLOR.LABEL')
          }}</span>
          <woot-color-picker v-model="config.widgetColor" />
        </label>
        <label class="block text-sm font-medium text-n-slate-12">
          <span class="mb-2 block">{{
            $t('INBOX_MGMT.WIDGET_BRAND_KIT.POSITION.LABEL')
          }}</span>
          <span
            class="inline-flex rounded-md bg-n-slate-3 p-1 dark:bg-n-solid-3"
          >
            <button
              v-for="position in positions"
              :key="position.value"
              type="button"
              class="rounded px-3 py-1.5 text-sm"
              :class="
                config.position === position.value
                  ? 'bg-n-alpha-1 text-n-slate-12 shadow-sm dark:bg-n-solid-1'
                  : 'text-n-slate-11'
              "
              @click="config.position = position.value"
            >
              {{ position.label }}
            </button>
          </span>
        </label>
      </div>

      <div v-if="config.style === 'branded_pill'" class="mt-4">
        <woot-input
          v-model="config.label"
          :label="$t('INBOX_MGMT.WIDGET_BRAND_KIT.LABEL.LABEL')"
          :placeholder="$t('INBOX_MGMT.WIDGET_BRAND_KIT.LABEL.PLACEHOLDER')"
        />
      </div>
      <div v-if="isProfileBadge" class="mt-4 grid gap-4 sm:grid-cols-2">
        <woot-input
          v-model="config.profileName"
          :label="$t('INBOX_MGMT.WIDGET_BRAND_KIT.PROFILE.NAME.LABEL')"
          :placeholder="
            $t('INBOX_MGMT.WIDGET_BRAND_KIT.PROFILE.NAME.PLACEHOLDER')
          "
        />
        <woot-input
          v-model="config.profileTagline"
          :label="$t('INBOX_MGMT.WIDGET_BRAND_KIT.PROFILE.TAGLINE.LABEL')"
          :placeholder="
            $t('INBOX_MGMT.WIDGET_BRAND_KIT.PROFILE.TAGLINE.PLACEHOLDER')
          "
        />
        <p class="sm:col-span-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.PROFILE.AVATAR_HINT') }}
        </p>
      </div>
    </div>
  </section>
</template>
